import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Cpu, Shield, BarChart3, Wrench } from 'lucide-react';
import { technologySteps } from '../data/mock';

const iconMap = {
  Cpu, Shield, BarChart3, Wrench
};

const Technology = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">How Niloman HX Works</h1>
          <p className="text-xl text-cyan-50 max-w-3xl mx-auto">
            Advanced IoT technology combined with robust water purification for complete transparency and control
          </p>
        </div>
      </section>

      {/* Technology Steps */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {technologySteps.map((step, index) => {
              const Icon = iconMap[step.icon];
              const isEven = index % 2 === 0;

              return (
                <div 
                  key={step.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:direction-rtl' : ''}`}
                >
                  <div className={`${!isEven ? 'lg:order-2' : ''} space-y-6`}>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-cyan-600 mb-1">
                          STEP {step.id}
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900">
                          {step.title}
                        </h3>
                      </div>
                    </div>
                    
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {step.description}
                    </p>

                    {step.id === 1 && (
                      <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-cyan-500">
                        <h4 className="font-semibold text-gray-900 mb-3">Sensors Include:</h4>
                        <ul className="space-y-2 text-gray-600">
                          <li>• TDS (Total Dissolved Solids) sensors</li>
                          <li>• Flow rate monitors</li>
                          <li>• Tank level indicators</li>
                          <li>• Filter life trackers</li>
                          <li>• Leak detection sensors</li>
                        </ul>
                      </div>
                    )}

                    {step.id === 3 && (
                      <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-cyan-500">
                        <h4 className="font-semibold text-gray-900 mb-3">Dashboard Features:</h4>
                        <ul className="space-y-2 text-gray-600">
                          <li>• Real-time TDS readings</li>
                          <li>• Daily/monthly water consumption</li>
                          <li>• Filter replacement countdown</li>
                          <li>• System health status</li>
                          <li>• Usage pattern analytics</li>
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className={`${!isEven ? 'lg:order-1' : ''} relative`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl blur-2xl opacity-20 -z-10"></div>
                    <img 
                      src={step.image}
                      alt={step.title}
                      className="rounded-3xl shadow-2xl w-full h-[400px] object-cover border-4 border-white"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Security & Reliability Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Security & Reliability
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your data security and system reliability are our top priorities
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-cyan-400 transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="h-7 w-7 text-cyan-600" />
                </div>
                <CardTitle>End-to-End Encryption</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  All data transmitted from your device to our cloud servers is encrypted using industry-standard AES-256 encryption protocols.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-cyan-400 transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-xl flex items-center justify-center mb-4">
                  <Cpu className="h-7 w-7 text-cyan-600" />
                </div>
                <CardTitle>Cloud Redundancy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Your data is backed up across multiple secure cloud servers with 99.9% uptime guarantee and automatic failover systems.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-cyan-400 transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-xl flex items-center justify-center mb-4">
                  <BarChart3 className="h-7 w-7 text-cyan-600" />
                </div>
                <CardTitle>Continuous Monitoring</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Our systems continuously monitor device health and connectivity status, alerting you immediately of any issues.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Infographic Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-teal-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl p-12">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              The Complete IoT Water Purification Ecosystem
            </h2>
            
            <div className="relative">
              <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-cyan-200 via-teal-300 to-cyan-200 -translate-y-1/2 hidden lg:block"></div>
              
              <div className="grid lg:grid-cols-4 gap-8 relative">
                {[
                  { title: 'Smart Sensors', icon: Cpu, desc: 'Real-time monitoring' },
                  { title: 'Cloud Platform', icon: BarChart3, desc: 'Secure data storage' },
                  { title: 'Mobile App', icon: Shield, desc: 'Remote control' },
                  { title: 'Auto Service', icon: Wrench, desc: 'Predictive alerts' }
                ].map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="text-center relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <Icon className="h-10 w-10 text-white" />
                      </div>
                      <h3 className="font-bold text-lg text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Technology;
